import { Component } from '@theme/component';

/**
 * @typedef {object} FloatingVideoRefs
 * @property {HTMLVideoElement} preview - The small muted looping video
 * @property {HTMLDialogElement} videoDialog - The fullscreen dialog
 *   (not named "dialog": the quick add popup's own component uses that ref and picked up this dialog instead)
 * @property {HTMLVideoElement} fullVideo - The fullscreen video with sound and controls
 */

/**
 * A small muted video that floats in a corner of the screen.
 * Clicking it opens the video fullscreen, muted; the unmute button opens it fullscreen with sound.
 * @extends {Component<FloatingVideoRefs>}
 */
class FloatingVideo extends Component {
  requiredRefs = ['preview', 'videoDialog', 'fullVideo'];

  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  #resizeObserver = new ResizeObserver(() => this.#syncLayoutVars());
  #ownsLayoutVars = false;

  connectedCallback() {
    // The quick add popup loads the product page's content, including this block; the video belongs on the product page only
    if (this.closest('quick-add-dialog, .quick-add-modal')) {
      queueMicrotask(() => this.remove());
      return;
    }

    // Live directly under <body> so no section's stacking context can cover the video
    if (this.parentElement !== document.body) {
      queueMicrotask(() => this.#moveToBody());
      return;
    }

    super.connectedCallback();

    this.refs.videoDialog.addEventListener('close', this.#handleDialogClose);
    this.refs.videoDialog.addEventListener('click', this.#handleDialogClick);

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:section:unload', this.#handleSectionUnload);
    }

    this.hidden = false;
    this.#resizeObserver.observe(this);
    this.#playPreview();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#resizeObserver.disconnect();
    this.#clearLayoutVars();

    this.refs.videoDialog?.removeEventListener('close', this.#handleDialogClose);
    this.refs.videoDialog?.removeEventListener('click', this.#handleDialogClick);
    document.removeEventListener('shopify:section:unload', this.#handleSectionUnload);
  }

  #moveToBody() {
    if (!this.isConnected) return;

    const existing = [...document.querySelectorAll('body > floating-video')].find((element) => element !== this);

    // The section was re-rendered (e.g. a variant change): keep the video that's already playing
    if (existing instanceof HTMLElement && existing.dataset.videoId === this.dataset.videoId) {
      this.remove();
      return;
    }

    existing?.remove();
    document.body.append(this);
  }

  /** @param {Event & { detail?: { sectionId?: string } }} event */
  #handleSectionUnload = (event) => {
    // In the theme editor, remove the moved video when its section reloads or is removed
    if (event.detail?.sectionId === this.dataset.sectionId) this.remove();
  };

  open() {
    this.#openFullscreen({ withSound: false });
  }

  openWithSound() {
    this.#openFullscreen({ withSound: true });
  }

  /** @param {{ withSound: boolean }} options */
  #openFullscreen({ withSound }) {
    const { videoDialog, fullVideo, preview } = this.refs;

    preview.pause();
    videoDialog.showModal();
    fullVideo.muted = !withSound;
    fullVideo.currentTime = 0;
    fullVideo.play().catch(() => {});
  }

  close() {
    this.refs.videoDialog.close();
  }

  /** Hides the video for this page view only; it comes back on refresh or the next page */
  dismiss() {
    this.refs.preview.pause();
    this.hidden = true;
    this.#resizeObserver.disconnect();
    this.#clearLayoutVars();
  }

  #handleDialogClose = () => {
    this.refs.fullVideo.pause();
    this.#playPreview();
  };

  /** @param {MouseEvent} event */
  #handleDialogClick = (event) => {
    // A click on the dark area around the video closes it
    if (event.target === this.refs.videoDialog) this.close();
  };

  #playPreview() {
    const { preview } = this.refs;

    if (this.#reducedMotion.matches) return;

    preview.muted = true;
    preview.play().catch(() => {});
  }

  /** Shares the video's height and right edge on <body> so the WhatsApp button can line up with it */
  #syncLayoutVars() {
    const { style } = document.body;

    style.setProperty('--floating-video-height', `${this.offsetHeight}px`);

    if (this.classList.contains('floating-video--bottom-right')) {
      style.setProperty('--floating-video-right', `${parseFloat(getComputedStyle(this).right) || 0}px`);
    }

    this.#ownsLayoutVars = true;
  }

  #clearLayoutVars() {
    if (!this.#ownsLayoutVars) return;

    document.body.style.removeProperty('--floating-video-height');
    document.body.style.removeProperty('--floating-video-right');
    this.#ownsLayoutVars = false;
  }
}

if (!customElements.get('floating-video')) {
  customElements.define('floating-video', FloatingVideo);
}
